import { Component, ElementRef, HostBinding, HostListener, Inject, OnInit } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Orientation } from 'shared/models/idt-components/idt-component.models';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';

import { MenuAnimatedItem, PieceMenuAnimatedSettings } from './models/piece-menu-animated.models';


@Component({
  selector: 'piece-animated-menu-render',
  templateUrl: './piece-animated-menu-render.component.html',
  styleUrls: ['./piece-animated-menu-render.component.scss']
})
export class PieceAnimatedMenuRenderComponent
  extends BaseRenderComponent<PieceMenuAnimatedSettings>
  implements OnInit {

  public selectedItem: number = 0;
  public orientation$!: Observable<Orientation>;

  constructor(
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly elRef: ElementRef<HTMLElement>,
    private readonly renderByDeviceService: RenderByDeviceService,
    @Inject('Window') private readonly window: Window
  ) {
    super();
  }

  @HostBinding('style.--imageWidth')
  public get imageWidth(): string {
    return `${this.elRef.nativeElement.offsetWidth}px`;
  }

  @HostBinding('style.--imageHeight')
  public get imageHeight(): string {
    const height = this.elRef.nativeElement.offsetHeight < this.window.innerHeight
      ? this.elRef.nativeElement.offsetHeight
      : this.window.innerHeight;
    return `${height}px`;
  }

  @HostBinding('class.dark-theme')
  public get isDarkTheme(): boolean {
    return this.settings.general?.theme === 'dark';
  }

  @HostBinding('class.light-theme')
  public get isLightTheme(): boolean {
    return this.settings.general?.theme === 'light';
  }

  @HostBinding('class.light-blend')
  public get isBlendTheme(): boolean {
    return this.settings.general?.theme === 'blend';
  }

  @HostBinding('class.mobile')
  public get isMobileBreakpoint(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public ngOnInit(): void {
    this.onRecalculate();
    this.orientation$ = (this.idtSharedRenderService.screenOrientation$ as Observable<Orientation>).pipe(
      distinctUntilChanged()
    );
    this.elRef.nativeElement.style.setProperty('--cropImageHeight', `${this.window.innerHeight}px`);
  }

  public getBackgroundImageSrc(item: MenuAnimatedItem, orientation: Orientation): string {
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const image = orientation === 'landscape' ? item.landscapeImage : item.portraitImage;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
      width: this.assetsSharedService.getWidth(this.elRef)
    };

    const imageSrc = this.assetsSharedService.getAssetUrl(image.image!, assetsUrl, akamaiOptions);
    return imageSrc ? `url(${imageSrc})` : 'none';
  }

  public setSelectedItem(index: number): void {
    this.selectedItem = index;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('window:resize', ['$event'])
  private onRecalculate(): void {
    const pieceDimensions = this.elRef.nativeElement.getBoundingClientRect();
    const imagePosition = pieceDimensions.top < 0 ? 'fixed' : 'absolute';
    const topImageValue = pieceDimensions.top < 0 ? '0' : 'auto';
    const imageHeight = pieceDimensions.height < 0 ? 'inherit' : `${pieceDimensions.height}px`;
    const cropImageHeight = pieceDimensions.top < 0 ? `${pieceDimensions.height - Math.abs(pieceDimensions.y)}px` : imageHeight;

    this.elRef.nativeElement.style.setProperty('--imagePosition', imagePosition);
    this.elRef.nativeElement.style.setProperty('--topImageValue', topImageValue);
    this.elRef.nativeElement.style.setProperty('--cropImageHeight', cropImageHeight);
  }

}
